import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatCard, fmtEUR, fmtInt } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, LineChart, Line } from "recharts";
import { toast } from "sonner";
import { Upload, Plus, Info, Trash2, Clock, CheckCircle2, Pencil, HelpCircle, BarChart3 } from "lucide-react";
import { ImportWizard, ImportHistory } from "./ImportWizard";
import { ProductAnalytics } from "./ProductAnalytics";
import { useAuth } from "@/context/AuthContext";

const nowHM = () => { const d = new Date(); return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`; };
const todayISO = () => new Date().toISOString().slice(0,10);

const emptyDs = () => ({
  business_date: todayISO(),
  total_revenue: "",
  receipt_count: "",
  cash_amount: "",
  card_amount: "",
  other_amount: "",
  fiscal_closure_ref: "",
  notes: "",
});

export default function Sales() {
  // FASE 7.4 STEP E.1 — action guards.
  const { can } = useAuth();
  const canDsWrite = can("daily_sales.write");
  const canCashWrite = can("cash_readings.write");
  const canImportCsv = can("sales.import");
  const canDeleteConfirmed = can("sales.delete_confirmed");
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [dsAnalytics, setDsAnalytics] = useState(null);
  const inputRef = useRef();

  // Cash register readings
  const [crOpen, setCrOpen] = useState(false);
  const [crForm, setCrForm] = useState({ business_date: todayISO(), reading_time: nowHM(), cumulative_total: "", note: "", is_closing: false });
  const [todayBands, setTodayBands] = useState(null);
  const [todayReadings, setTodayReadings] = useState([]);
  const [periodStats, setPeriodStats] = useState(null);
  const [periodDays, setPeriodDays] = useState("7");

  // Daily sales confirmed
  const [dsOpen, setDsOpen] = useState(false);
  const [dsEditing, setDsEditing] = useState(null); // {id} when editing
  const [dsForm, setDsForm] = useState(emptyDs());
  const [dsList, setDsList] = useState([]);
  const [dsHistoryDays, setDsHistoryDays] = useState("30");
  const [compareToday, setCompareToday] = useState(null);

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [importRefresh, setImportRefresh] = useState(0);

  const load = async (d) => {
    try {
      const [{ data: detailed }, { data: dsA }] = await Promise.all([
        api.get(`/sales/analytics?days=${d}`),
        api.get(`/daily-sales/analytics?days=${d}`),
      ]);
      setData(detailed);
      setDsAnalytics(dsA);
    } catch (e) {
      console.error("[Sales] load failed", e);
      toast.error("Errore caricamento vendite");
    }
  };
  const loadCash = async () => {
    try {
      const [today, readings, period, compare] = await Promise.all([
        api.get(`/cash-readings/day/${todayISO()}`),
        api.get(`/cash-readings`, { params: { business_date: todayISO() } }),
        api.get(`/cash-readings/period`, { params: { days: Number(periodDays) } }),
        api.get(`/daily-sales/compare/${todayISO()}`),
      ]);
      setTodayBands(today.data);
      setTodayReadings(readings.data);
      setPeriodStats(period.data);
      setCompareToday(compare.data);
    } catch (e) {
      console.error("[Sales] loadCash failed", e);
    }
  };
  const loadDsHistory = async () => {
    try {
      const { data } = await api.get(`/daily-sales`, { params: { days: Number(dsHistoryDays) } });
      setDsList(data);
    } catch (e) { console.error("[Sales] loadDsHistory failed", e); }
  };
  useEffect(() => { load(days); }, [days]);
  useEffect(() => { loadCash(); }, [periodDays]);
  useEffect(() => { loadDsHistory(); }, [dsHistoryDays]);

  const submitCr = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cash-readings", {
        business_date: crForm.business_date,
        reading_time: crForm.reading_time,
        cumulative_total: Number(crForm.cumulative_total),
        note: crForm.note?.trim() || null,
        is_closing: crForm.is_closing,
      });
      toast.success("Lettura registrata");
      setCrOpen(false);
      setCrForm({ business_date: todayISO(), reading_time: nowHM(), cumulative_total: "", note: "", is_closing: false });
      loadCash();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };
  const delReading = async (id) => {
    if (!window.confirm("Eliminare la lettura?")) return;
    try { await api.delete(`/cash-readings/${id}`); loadCash(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const submitDs = async (e) => {
    e.preventDefault();
    const payload = {
      business_date: dsForm.business_date,
      total_revenue: Number(dsForm.total_revenue),
      receipt_count: dsForm.receipt_count === "" ? null : Number(dsForm.receipt_count),
      cash_amount: dsForm.cash_amount === "" ? null : Number(dsForm.cash_amount),
      card_amount: dsForm.card_amount === "" ? null : Number(dsForm.card_amount),
      other_amount: dsForm.other_amount === "" ? null : Number(dsForm.other_amount),
      fiscal_closure_ref: dsForm.fiscal_closure_ref?.trim() || null,
      notes: dsForm.notes?.trim() || null,
    };
    try {
      if (dsEditing?.id) {
        await api.put(`/daily-sales/${dsEditing.id}`, payload);
        toast.success("Chiusura aggiornata");
      } else {
        await api.post("/daily-sales", payload);
        toast.success("Chiusura registrata");
      }
      setDsOpen(false); setDsEditing(null); setDsForm(emptyDs());
      loadCash(); loadDsHistory(); load(days);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "daily_sales_exists") {
        if (window.confirm(detail.message + "\n\nCarico la chiusura esistente per modifica.")) {
          try {
            const { data: existing } = await api.get(`/daily-sales/by-date/${payload.business_date}`);
            if (existing) openEditDs(existing);
          } catch (e2) { console.error(e2); }
        }
      } else {
        toast.error(typeof detail === "string" ? detail : "Errore");
      }
    }
  };

  const openNewDs = () => {
    setDsEditing(null); setDsForm(emptyDs()); setDsOpen(true);
  };
  const openEditDs = (row) => {
    setDsEditing({ id: row.id });
    setDsForm({
      business_date: row.business_date,
      total_revenue: String(row.total_revenue ?? ""),
      receipt_count: row.receipt_count == null ? "" : String(row.receipt_count),
      cash_amount: row.cash_amount == null ? "" : String(row.cash_amount),
      card_amount: row.card_amount == null ? "" : String(row.card_amount),
      other_amount: row.other_amount == null ? "" : String(row.other_amount),
      fiscal_closure_ref: row.fiscal_closure_ref || "",
      notes: row.notes || "",
    });
    setDsOpen(true);
  };
  const delDs = async (row) => {
    if (!window.confirm(`Eliminare la chiusura del ${row.business_date}?\nQuesta azione è tracciata in audit.`)) return;
    try {
      await api.delete(`/daily-sales/${row.id}`);
      toast.success("Chiusura eliminata");
      loadCash(); loadDsHistory(); load(days);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const upload = async (e) => {
    // Deprecated one-click legacy path — apre wizard invece che uploadare direttamente
    setWizardOpen(true);
    if (inputRef.current) inputRef.current.value = "";
  };

  // Decide which analytics to display: confirmed wins when present.
  const useConfirmed = (dsAnalytics?.confirmed_count || 0) > 0;
  const stats = useConfirmed ? {
    revenue: dsAnalytics.total_revenue,
    receipts: dsAnalytics.receipts_total,
    avg_ticket: dsAnalytics.avg_ticket,
    items_sold: null,  // UNKNOWN, not zero
    source_label: "Chiusure confermate",
    cash: dsAnalytics.cash_total,
    card: dsAnalytics.card_total,
    other: dsAnalytics.other_total,
  } : {
    revenue: data?.total_revenue ?? 0,
    receipts: data?.transactions ?? 0,
    avg_ticket: data?.avg_ticket ?? 0,
    items_sold: data ? (data.by_day?.reduce((s, d) => s + (d.count || 0), 0) > 0 ? data.transactions * (data.items_per_ticket || 0) : null) : null,
    source_label: "Vendite dettagliate",
    cash: null, card: null, other: null,
  };

  return (
    <div data-testid="sales-page">
      <PageHeader eyebrow="Fatturato" title="Vendite" subtitle="Chiusure giornaliere, letture cassa e vendite dettagliate"
        actions={
          <div className="flex gap-2 flex-wrap">
            {/* + Chiusura vendite */}
            {canDsWrite && (
            <Dialog open={dsOpen} onOpenChange={(o) => { setDsOpen(o); if (!o) { setDsEditing(null); setDsForm(emptyDs()); } }}>
              <DialogTrigger asChild>
                <Button data-testid="new-daily-sales-btn" onClick={openNewDs} className="bg-emerald-700 hover:bg-emerald-800 text-[#FDFBF7]"><CheckCircle2 size={14} /> Chiusura vendite</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="font-serif">{dsEditing ? "Modifica chiusura vendite" : "Nuova chiusura vendite"}</DialogTitle></DialogHeader>
                <form onSubmit={submitDs} className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Data</Label><Input type="date" value={dsForm.business_date} onChange={(e) => setDsForm({ ...dsForm, business_date: e.target.value })} required data-testid="ds-date" /></div>
                    <div><Label>Fatturato totale €</Label><Input autoFocus type="number" step="0.01" min="0" value={dsForm.total_revenue} onChange={(e) => setDsForm({ ...dsForm, total_revenue: e.target.value })} placeholder="es. 612.50" required data-testid="ds-total" /></div>
                  </div>
                  <div><Label>N° scontrini <span className="text-neutral-400 text-xs">(opzionale)</span></Label><Input type="number" min="0" step="1" value={dsForm.receipt_count} onChange={(e) => setDsForm({ ...dsForm, receipt_count: e.target.value })} placeholder="es. 47" data-testid="ds-receipts" /></div>
                  <div className="border-t border-[#E2D9C8] pt-2">
                    <div className="label-eyebrow mb-1 flex items-center gap-1">Ripartizione pagamenti <span className="text-[10px] text-neutral-400">opzionale · somma = totale ±€0,01</span></div>
                    <div className="grid grid-cols-3 gap-2">
                      <div><Label className="text-xs">Contanti €</Label><Input type="number" step="0.01" min="0" value={dsForm.cash_amount} onChange={(e) => setDsForm({ ...dsForm, cash_amount: e.target.value })} data-testid="ds-cash" /></div>
                      <div><Label className="text-xs">Carta/POS €</Label><Input type="number" step="0.01" min="0" value={dsForm.card_amount} onChange={(e) => setDsForm({ ...dsForm, card_amount: e.target.value })} data-testid="ds-card" /></div>
                      <div><Label className="text-xs">Altri €</Label><Input type="number" step="0.01" min="0" value={dsForm.other_amount} onChange={(e) => setDsForm({ ...dsForm, other_amount: e.target.value })} data-testid="ds-other" /></div>
                    </div>
                  </div>
                  <div><Label>Rif. chiusura fiscale <span className="text-neutral-400 text-xs">(opzionale)</span></Label><Input value={dsForm.fiscal_closure_ref} onChange={(e) => setDsForm({ ...dsForm, fiscal_closure_ref: e.target.value })} placeholder="es. Z-047" data-testid="ds-fiscal-ref" /></div>
                  <div><Label>Note</Label><Textarea rows={2} value={dsForm.notes} onChange={(e) => setDsForm({ ...dsForm, notes: e.target.value })} data-testid="ds-notes" /></div>
                  <div className="text-[11px] text-neutral-500 bg-[#EEF6EE] rounded p-2 flex gap-2"><Info size={12} className="mt-0.5" /><span>La chiusura è il dato <b>ufficiale</b> del giorno. Le letture cassa restano visibili come dettaglio per fascia ma non vengono modificate.</span></div>
                  <DialogFooter><Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-[#FDFBF7]" data-testid="ds-submit">{dsEditing ? "Aggiorna" : "Salva"}</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            )}

            {/* + Lettura cassa */}
            {canCashWrite && (
            <Dialog open={crOpen} onOpenChange={setCrOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-cash-reading-btn" variant="outline"><Plus size={14} /> Lettura cassa</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-serif">Nuova lettura cassa</DialogTitle></DialogHeader>
                <form onSubmit={submitCr} className="space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label>Data</Label><Input type="date" value={crForm.business_date} onChange={(e) => setCrForm({ ...crForm, business_date: e.target.value })} data-testid="cr-date" /></div>
                    <div><Label>Ora</Label><Input type="time" value={crForm.reading_time} onChange={(e) => setCrForm({ ...crForm, reading_time: e.target.value })} data-testid="cr-time" /></div>
                    <div><Label>Cumulativo €</Label><Input autoFocus type="number" step="0.01" min="0" value={crForm.cumulative_total} onChange={(e) => setCrForm({ ...crForm, cumulative_total: e.target.value })} placeholder="es. 420" required data-testid="cr-amount" /></div>
                  </div>
                  <div><Label>Nota (opzionale)</Label><Textarea rows={2} value={crForm.note} onChange={(e) => setCrForm({ ...crForm, note: e.target.value })} /></div>
                  <div className="flex items-center gap-2"><input id="cr-closing" type="checkbox" checked={crForm.is_closing} onChange={(e) => setCrForm({ ...crForm, is_closing: e.target.checked })} /><Label htmlFor="cr-closing">Chiusura giornata</Label></div>
                  <div className="text-[11px] text-neutral-500 bg-[#F3EDE1] rounded p-2 flex gap-2"><Info size={12} className="mt-0.5" /><span>Snapshot cumulativo non-fiscale. Il sistema calcola le fasce per differenza tra letture consecutive.</span></div>
                  <DialogFooter><Button type="submit" className="bg-neutral-900 text-[#FDFBF7]" data-testid="cr-submit">Salva</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            )}
            <input ref={inputRef} type="file" accept=".csv" className="hidden" onChange={upload} />
            {canImportCsv && (
              <Button variant="outline" onClick={()=>setWizardOpen(true)} data-testid="import-csv-btn"><Upload size={14} /> Import CSV</Button>
            )}
            <Link to="/product-performance" data-testid="link-product-performance">
              <Button variant="outline"><BarChart3 size={14} /> Performance Prodotti</Button>
            </Link>
          </div>
        } />

      <ImportWizard open={wizardOpen} onOpenChange={setWizardOpen} onDone={()=>{ setImportRefresh(x=>x+1); load(days); loadCash(); loadDsHistory(); }} />
      <ImportHistory refreshKey={importRefresh} onUndone={()=>{ setImportRefresh(x=>x+1); load(days); }} />
      <ProductAnalytics refreshKey={importRefresh} />

      {/* Confronto lettura vs chiusura (oggi) */}
      {compareToday?.has_both && (
        <div className="border border-[#E2D9C8] rounded-lg p-4 bg-[#FDFBF7] mb-6" data-testid="today-compare">
          <div className="flex justify-between items-center flex-wrap gap-2 text-sm">
            <div>
              <div className="label-eyebrow mb-1">Confronto lettura vs chiusura · oggi</div>
              <div className="flex gap-4 items-baseline">
                <span>Lettura cassa: <b>{fmtEUR(compareToday.cash_reading_total)}</b>{compareToday.cash_reading_partial && <span className="text-[10px] px-1 bg-amber-100 text-amber-800 rounded ml-1">parziale</span>}</span>
                <span>Chiusura confermata: <b className="text-emerald-800">{fmtEUR(compareToday.confirmed_total)}</b></span>
                <span className={compareToday.delta_confirmed_minus_reading === 0 ? "" : "text-amber-800"}>Scostamento: <b>{compareToday.delta_confirmed_minus_reading > 0 ? "+" : ""}{fmtEUR(compareToday.delta_confirmed_minus_reading)}</b></span>
              </div>
            </div>
            <span className="text-[11px] text-neutral-500">Utile per individuare errori d'inserimento. Nessuna rettifica automatica sulle fasce.</span>
          </div>
        </div>
      )}

      {/* Fatturato per fascia — OGGI */}
      <div className="border border-[#E2D9C8] rounded-lg p-6 bg-white mb-6" data-testid="today-bands">
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="label-eyebrow">Oggi · fatturato per fascia <span className="text-[10px] text-neutral-500 normal-case">(letture cassa cumulative)</span></div>
            <div className="font-serif text-2xl mt-1">{fmtEUR(todayBands?.total_daily_revenue || 0)}</div>
          </div>
          <div className="text-xs text-neutral-500">{todayBands?.readings_count || 0} letture</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {todayBands && Object.entries(todayBands.bands || {}).map(([name, b]) => (
            <div key={name} className="border border-[#E2D9C8] rounded p-3">
              <div className="label-eyebrow capitalize flex items-center gap-1">
                {name}
                {b.partial && <span className="text-[10px] px-1 bg-amber-100 text-amber-800 rounded" title={`ultimo dato alle ${b.last_reading_time || "—"}`}>parziale</span>}
              </div>
              <div className="text-[10px] text-neutral-500 mt-0.5">{b.band_start}–{b.band_end}</div>
              <div className="font-serif text-xl mt-1">{fmtEUR(b.revenue)}</div>
            </div>
          ))}
        </div>
        {todayReadings.length > 0 && (
          <div className="mt-4 pt-3 border-t border-[#E2D9C8]">
            <div className="text-xs text-neutral-500 mb-2">Letture di oggi</div>
            <ul className="space-y-1 text-sm">
              {todayReadings.map((r) => (
                <li key={r.id} className="flex justify-between items-center" data-testid={`cr-row-${r.id}`}>
                  <span><Clock size={11} className="inline mr-1" /> {r.reading_time} · <span className="font-medium">{fmtEUR(r.cumulative_total)}</span>{r.is_closing && <span className="ml-2 text-[10px] px-1 bg-emerald-100 text-emerald-800 rounded">chiusura</span>}{r.note && <span className="text-neutral-500 ml-2">{r.note}</span>}</span>
                  <button onClick={() => delReading(r.id)} disabled={!canDeleteConfirmed} className={`text-neutral-400 ${canDeleteConfirmed ? "hover:text-red-700" : "opacity-30 cursor-not-allowed"}`}><Trash2 size={12} /></button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Storico per fascia (invariato) */}
      {periodStats && periodStats.days_with_data > 0 && (
        <div className="border border-[#E2D9C8] rounded-lg p-6 bg-white mb-6" data-testid="period-bands">
          <div className="flex justify-between items-center mb-4">
            <div>
              <div className="label-eyebrow">Storico per fascia</div>
              <div className="font-serif text-xl mt-1">{fmtEUR(periodStats.total_revenue)} · media {fmtEUR(periodStats.avg_daily_revenue)}/g</div>
              <div className="text-xs text-neutral-500 mt-1">Basato su {periodStats.days_with_data} giorni con letture</div>
            </div>
            <Select value={periodDays} onValueChange={setPeriodDays}>
              <SelectTrigger className="w-32" data-testid="period-selector"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 giorni</SelectItem>
                <SelectItem value="30">30 giorni</SelectItem>
                <SelectItem value="90">90 giorni</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(periodStats.per_band || {}).map(([name, b]) => (
              <div key={name} className="border border-[#E2D9C8] rounded p-3">
                <div className="label-eyebrow capitalize">{name}</div>
                <div className="font-serif text-lg mt-1">{fmtEUR(b.total)}</div>
                <div className="text-xs text-neutral-500">Media {fmtEUR(b.avg)}/g · {b.pct_of_daily}%</div>
                <div className="text-[10px] text-neutral-400 mt-1">{b.days_with_data} giorni con dati</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Statistiche (usa daily_sales_confirmed se disponibili, altrimenti detailed) */}
      <div className="mb-6" data-testid="sales-stats-block">
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow flex items-center gap-2">Statistiche vendite
            <span className={"text-[10px] px-2 py-0.5 rounded " + (useConfirmed ? "bg-emerald-100 text-emerald-800" : "bg-neutral-100 text-neutral-600")}>
              {stats.source_label}
            </span>
            {useConfirmed && <span className="text-[10px] text-neutral-500">· {dsAnalytics.confirmed_count} chiusure in {days} giorni</span>}
          </div>
          <div className="flex gap-2 flex-wrap">
            {["7","30","90"].map((n) => (
              <Button key={n} size="sm" variant={days===n?"default":"outline"} onClick={() => setDays(n)} data-testid={`days-${n}`}>{n} giorni</Button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Fatturato totale" value={fmtEUR(stats.revenue || 0)} data-testid="stat-revenue" />
          <StatCard label="N. scontrini" value={stats.receipts == null ? "—" : fmtInt(stats.receipts)} data-testid="stat-receipts" />
          <StatCard label="Scontrino medio" value={stats.avg_ticket == null ? "—" : fmtEUR(stats.avg_ticket)} data-testid="stat-avg-ticket" />
          <div className="border border-[#E2D9C8] rounded-lg p-4 bg-white" data-testid="stat-items-sold">
            <div className="label-eyebrow flex items-center gap-1">Prodotti venduti {stats.items_sold == null && <HelpCircle size={11} className="text-neutral-400" />}</div>
            <div className="font-serif text-2xl mt-1">{stats.items_sold == null ? <span className="text-neutral-400">Dato non disponibile</span> : fmtInt(stats.items_sold)}</div>
            {stats.items_sold == null && <div className="text-[10px] text-neutral-400 mt-1">Serve tracciamento per riga</div>}
          </div>
        </div>
        {useConfirmed && (stats.cash != null || stats.card != null || stats.other != null) && (
          <div className="grid grid-cols-3 gap-3 mt-3">
            <StatCard label="Contanti" value={stats.cash == null ? "—" : fmtEUR(stats.cash)} />
            <StatCard label="Carta/POS" value={stats.card == null ? "—" : fmtEUR(stats.card)} />
            <StatCard label="Altri" value={stats.other == null ? "—" : fmtEUR(stats.other)} />
          </div>
        )}
      </div>

      {/* Storico chiusure giornaliere */}
      <div className="border border-[#E2D9C8] rounded-lg p-6 bg-white mb-6" data-testid="ds-history">
        <div className="flex justify-between items-center mb-3">
          <div>
            <div className="label-eyebrow">Storico chiusure giornaliere</div>
            <div className="text-xs text-neutral-500 mt-1">{dsList.length} chiusura/e negli ultimi {dsHistoryDays} giorni</div>
          </div>
          <Select value={dsHistoryDays} onValueChange={setDsHistoryDays}>
            <SelectTrigger className="w-32" data-testid="ds-history-selector"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 giorni</SelectItem>
              <SelectItem value="30">30 giorni</SelectItem>
              <SelectItem value="90">90 giorni</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {dsList.length === 0 ? (
          <div className="text-sm text-neutral-500 py-4">Nessuna chiusura registrata nel periodo.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-neutral-500 text-xs uppercase">
                <tr>
                  <th className="py-2">Data</th>
                  <th className="py-2 text-right">Fatturato</th>
                  <th className="py-2 text-right">Scontrini</th>
                  <th className="py-2 text-right">Medio</th>
                  <th className="py-2 text-right">Contanti</th>
                  <th className="py-2 text-right">POS</th>
                  <th className="py-2">Stato</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {dsList.map((r) => {
                  const avg = r.receipt_count && r.receipt_count > 0 ? r.total_revenue / r.receipt_count : null;
                  return (
                    <tr key={r.id} className="border-t border-[#E2D9C8]" data-testid={`ds-row-${r.business_date}`}>
                      <td className="py-2">{r.business_date}</td>
                      <td className="py-2 text-right font-medium">{fmtEUR(r.total_revenue)}</td>
                      <td className="py-2 text-right">{r.receipt_count == null ? "—" : fmtInt(r.receipt_count)}</td>
                      <td className="py-2 text-right">{avg == null ? "—" : fmtEUR(avg)}</td>
                      <td className="py-2 text-right">{r.cash_amount == null ? "—" : fmtEUR(r.cash_amount)}</td>
                      <td className="py-2 text-right">{r.card_amount == null ? "—" : fmtEUR(r.card_amount)}</td>
                      <td className="py-2"><span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Confermato</span></td>
                      <td className="py-2 text-right">
                        {canDsWrite && <button onClick={() => openEditDs(r)} className="text-neutral-500 hover:text-neutral-900 mr-2" data-testid={`ds-edit-${r.business_date}`}><Pencil size={12} /></button>}
                        {canDeleteConfirmed && <button onClick={() => delDs(r)} className="text-neutral-400 hover:text-red-700" data-testid={`ds-del-${r.business_date}`}><Trash2 size={12} /></button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Grafico fatturato giornaliero */}
      {(useConfirmed ? dsAnalytics.by_day : data?.by_day)?.length > 0 && (
        <div className="border border-[#E2D9C8] rounded-lg p-4 bg-white mb-4" data-testid="daily-chart">
          <div className="label-eyebrow mb-2">Fatturato giornaliero · {stats.source_label}</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={useConfirmed ? dsAnalytics.by_day : data.by_day}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{fontSize:10}} />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="revenue" stroke={useConfirmed ? "#047857" : "#8B5A2B"} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
